import React from 'react';
import PropTypes from 'prop-types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPencilAlt, faPlus } from '@fortawesome/free-solid-svg-icons';

import Table from '../table';
import Notification from '../notification';
import BpUtils from '../../../lib/bputils';
import { compareString } from '../table/sort-helpers';
import Button from '../button';
import UserInvitationModal from '../user-invitation-modal';

import styles from './styles.css';


const compareUser = (a, b, valueFn, reverse = false) => {
  // if the user has no id then it is an invitation, invitations are always sorted after users
  if (!a.id && !b.id) {
    return compareString(a, b, valueFn);
  }

  if (a.id && !b.id) {
    // if the sort order is ascending we want the N/A to be at the end of the list
    return -1;
  }
  if (!a.id && b.id) {
    return 1;
  }
  return compareString(a, b, valueFn, reverse);
};

const PendingProperty = ({ children }) => <span className={styles.invitationProperty}>{children}</span>;

PendingProperty.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
};

export default function UserList() {
  const USER_COLUMNS = [
    {
      title: 'Name',
      value: user => (user.id ? `${user.first_name} ${user.last_name}` : 'Pending Invitation'),
      render: user => (user.id ? `${user.first_name} ${user.last_name}` : <PendingProperty>Pending Invitation</PendingProperty>),
      sortFn: compareUser,
      colSpan: 2,
    },
    {
      title: 'Email',
      value: user => (user.id ? user.email : user.invitation_email),
      render: user => (user.id ? user.email : <PendingProperty>{user.invitation_email}</PendingProperty>),
      sortFn: compareUser,
      colSpan: 2,
    },
    {
      title: 'Title',
      value: user => (user.id ? user.title : 'N/A'),
      render: user => (user.id ? user.title : <PendingProperty>N/A</PendingProperty>),
      sortFn: compareUser,
    },
    {
      title: 'Department',
      value: user => (user.id ? user.department : 'N/A'),
      render: user => (user.id ? <span className={styles.capitalize}>{user.department}</span> : <PendingProperty>N/A</PendingProperty>),
      sortFn: compareUser,
    },
    {
      title: 'Permission Level',
      value: user => (user.id ? user.roles[0] : 'N/A'),
      render: user => <span className={styles.capitalize}>{user.roles[0].replace('_', ' ')}</span>,
      sortFn: compareUser,
    },
    {
      title: '',
      render: user => (
        <Button size="sm" secondary onClick={() => handleOnEditClick(user)}>
          <FontAwesomeIcon icon={faPencilAlt} className={styles.faIcon} />
          <span>Edit</span>
        </Button>
      ),
    },
  ];

  const userFilters = [
    { name: 'All Users', filterFn: () => true },
    { name: 'Active Users', filterFn: user => !!user?.id },
    { name: 'Pending Invitations', filterFn: user => !user?.id },
  ];

  const [users, setUsers] = React.useState(null);
  const [invitations, setInvitations] = React.useState(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState([]);
  const [selectedUser, setSelectedUser] = React.useState(null);
  const [showModal, setShowModal] = React.useState(false);
  const [stationLists, setStationLists] = React.useState([]);

  React.useEffect(() => {
    getUsers();
    getInvitations();
    getStationLists();
  }, []);

  React.useEffect(() => {
    if (users && invitations) {
      setIsLoading(false);
      setData([
        ...users,
        ...invitations,
      ]);
    }
  }, [users, invitations]);

  const getUsers = async () => {
    await BpUtils.api.get({
      path: '/users',
      requestId: 'userListgetAllUsers',
      error() {
        Notification.error('Unable to fetch your users.');
        setUsers([]);
      },
      success(res) {
        setUsers(res.filter(r => !r.email.includes('gasbuddy')) || []);
      },
    });
  };

  const getInvitations = async () => {
    await BpUtils.api.get({
      path: '/users/invitations',
      requestId: 'userListgetAllInvitations',
      error() {
        Notification.error('Unable to fetch your invitations.');
        setInvitations([]);
      },
      success(pendingInvitations) {
        setInvitations(pendingInvitations?.pending.filter((pi => !pi.invitation_email.includes('gasbuddy'))) || []);
      },
    });
  };

  const getStationLists = async () => BpUtils.api.get({
    requestId: 'getAllStationLists',
    path: '/station-lists',
    success: (res) => {
      setStationLists(res);
    },
    error: () => {
      Notification.error('Unable to fetch your station lists');
    },
  });

  const handleOnEditClick = (user) => {
    setSelectedUser(user);
    setShowModal(true);
  };

  const handleOnAddUser = () => {
    setShowModal(true);
  };

  const handleOnUserInvitationModalClose = () => {
    setShowModal(false);
    setSelectedUser(null);
  };

  const handleOnInvitationDelete = (invitation) => {
    const remainingInvitations = invitations.filter(i => i.invitation_email !== invitation.invitation_email);
    setInvitations(remainingInvitations);
  };

  const handleOnUserDelete = (user) => {
    const remainingUsers = users.filter(u => u.id !== user.id);
    setUsers(remainingUsers);
  };

  const handleOnSave = () => {
    setUsers(null);
    setInvitations(null);
    setIsLoading(true);
    getUsers();
    getInvitations();
  };

  return (
    <>
      <Table
        title={(
          <span>
            <b>Users</b>
            {` (${data.length})`}
          </span>
      )}
        loading={isLoading}
        data={data}
        tableColumns={USER_COLUMNS}
        filters={userFilters}
        searchable
        CTAText={(
          <>
            <FontAwesomeIcon icon={faPlus} className={styles.faIcon} />
            <span>Add User</span>
          </>
        )}
        onCTAClick={handleOnAddUser}
        paginator
        minWidth={800}
      />
      <UserInvitationModal
        show={showModal}
        onSave={handleOnSave}
        onClose={handleOnUserInvitationModalClose}
        onDeleteInvitation={handleOnInvitationDelete}
        onDeleteUser={handleOnUserDelete}
        user={selectedUser}
        stationLists={stationLists}
      />
    </>
  );
}
